// src/layouts/ContentLayout.js
import React, { useState, useEffect } from "react";
import "./ContentLayout.css";
import { useHistory } from "react-router-dom";
import { safeGoBack } from "../utils/wkCommon";
import { isZh, getLang, setLang, LANG_ID, LANG_ZH } from "../utils/language";

import { useLocation } from "react-router-dom";
import { logVisit } from "../utils/wkCommon";

export default function ContentLayout({
  title = "Content",
  titleNode,
  headerActions,
  searchValue,
  onSearchChange,
  filterNode,
  infoNode,
  mainNode,
  detailNode,
  leftOpen: leftOpenProp,
  onCloseDetail,
  hideSearch = false,         // when true, hide the search row entirely
  hideHeaderActions = false,  // when true, don't show default add/more fallback in header-right
  singleColumn = false,       // when true, render mainNode centered and hide left/right panels
  hideLangSwitch = true
}) {
  // local search fallback (uncontrolled) if parent didn't provide searchValue/onSearchChange
  const history = useHistory();
  const [localSearch, setLocalSearch] = useState("");
  const search = typeof searchValue === "string" ? searchValue : localSearch;
  const setSearch = (v) => {
    if (typeof onSearchChange === "function") return onSearchChange(v);
    setLocalSearch(v);
  };

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);
  // start closed to avoid flash on mobile; we'll open immediately if desktop
  const [filterOpen, setFilterOpen] = useState(false);

  const [lang, setLangState] = React.useState(getLang());

  function toggleLang() {
    const newLang = lang === LANG_ZH ? LANG_ID : LANG_ZH;
    setLang(newLang);
    window.location.reload();
  }

  const location = useLocation();

  useEffect(() => {
    logVisit({
      path: location.pathname,
      query: location.search,
    });
  }, [location.pathname]);

  useEffect(() => {
    function onResize() {
      const desktopNow = window.innerWidth >= 900;
      setIsDesktop(desktopNow);
      // Open filter automatically on desktop, close it on mobile
      setFilterOpen(desktopNow);
    }

    // run once on mount to set initial desktop/open state correctly
    onResize();

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // leftOpen: respect explicit prop; otherwise open if desktop OR filterOpen (mobile toggled)
  const leftOpen = typeof leftOpenProp === "boolean" ? leftOpenProp : (filterOpen || isDesktop);

  // Backdrop flag for mobile
  const showBackdrop = !isDesktop && filterOpen;
  // show detail backdrop when in mobile and a detailNode is present
  const showDetailBackdrop = !isDesktop && !!detailNode;

  // The layout only — it places the slot nodes in their right places.
  return (
    <div className="content-layout">
      {/* Header (full bleed handled by CSS) */}
      <header className="content-header">
        <div className="header-inner">
          <div className="header-left">
            <button
              className="btn-icon back-btn"
              onClick={() => safeGoBack(history)}
              aria-label="Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="page-title">
              {titleNode ? titleNode : title}
            </div>
          </div>

          {/* headerActions slot: page supplies buttons (add/search/more) */}
          <div className="header-right">
            {!hideLangSwitch && (
              <button className="btn-icon" onClick={toggleLang} title="切換語言 Ubah Bahasa">
                {lang === LANG_ZH ? "ID" : "文"}
              </button>
            )}

            {headerActions ? headerActions : (
              hideHeaderActions ? null : (
              <>
                {/* fallback simple add/more */}
                <button className="btn-icon add-btn" title="Add">＋</button>
                <button className="btn-icon more-btn" title="More">⋯</button>
              </>
              )
            )}
          </div>
        </div>
      </header>

      {/* Search row (layout provides input, but can be controlled by page via props) */}
      {!hideSearch && (
      <div className="search-row">
        <div className="search-box">
          <input
            type="search"
            placeholder={isZh()? "請依姓名、暱稱或地址搜尋…" : "Search by name, nickname, or address..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </div>

        {/* On desktop the filter button is hidden via CSS; on mobile the page can show a button in headerActions if desired */}
        <div className="filter-area" aria-hidden={isDesktop}>
          <button
            className="btn-icon filter-btn-mobile"
            onClick={() => setFilterOpen(prev => !prev)}
            aria-haspopup="true"
            aria-expanded={filterOpen}
            title={isZh() ? "篩選條件" : "Filter"}
          >
            ☰
          </button>
          {/* parent may include a mobile filter button as part of headerActions; this is a fallback */}
        </div>
      </div>
      )}

      {/* Info / tip row slot */}
      {!hideSearch && (
      <div className="tip-area" role="note">
        {infoNode ? infoNode : <div className="tip-text"> {/* default empty */} </div>}
      </div>
      )}

      {/* Body grid: left filter, main, right detail */}
      <div className="content-body">
        {/* mobile backdrop (click to close mobile filter) */}
        {showBackdrop && <div className="panel-backdrop" onClick={() => setFilterOpen(false)} />}
        {showDetailBackdrop && <div className="panel-backdrop detail" onClick={() => { if (typeof onCloseDetail === 'function') onCloseDetail(); }} />}

        {/* Left filter panel slot - page provides full markup for filters */}
        {!singleColumn && (
        <aside
          id="filter-panel"
          className={`filter-panel ${leftOpen ? "open" : ""} ${isDesktop ? "desktop" : "mobile"}`}
          aria-hidden={!leftOpen && !isDesktop}
        >
          {/* mobile-only header close control (desktop CSS hides it) */}
          {!isDesktop && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong>{isZh() ? "篩選條件" : "Filters"}</strong>
              <button className="btn-icon small close-filter" onClick={() => setFilterOpen(false)} aria-label="Close filters">✕</button>
            </div>
          )}

          {/* If the page provided filterNode, render it; otherwise show a placeholder */}
          {filterNode ? (
            filterNode
          ) : (
            <div style={{ padding: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{isZh() ? "篩選條件" : "Filters"}</div>
              <div>{isZh() ? "未提供篩選條件" : "No filters provided"}</div>
            </div>
          )}
        </aside>
        )}

        {/* MAIN content slot (page fully controls rendering of rows and selection) */}
        <main className="content-list" role="main">
          {mainNode ? mainNode : <div style={{ padding: 20 }}>{isZh() ? "未提供主要內容。" : "No main content supplied."}</div>}
        </main>

        {/* Right detail panel slot */}
        {!singleColumn && detailNode && (
        <aside className={`detail-panel ${isDesktop ? "desktop" : "mobile"} ${detailNode ? "open" : ""}`}>
          {detailNode ? detailNode : <div className="detail-empty">{isZh() ? "請選擇一筆資料以查看詳細內容。" : "Select a row to see details"}</div>}
        </aside>
        )}
      </div>

      {/* footer strip (layout only) */}
      <div className="content-footer-strip" />
    </div>
  );
}
